import type { Font } from 'opentype.js';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { htmlToRuns, type StyledRun } from '../boardLayout';
import { chalkFontUrl, loadChalkFace, loadChalkFont } from './chalkAssets';

// Canonical handwriting reveal (ported from the admin teach page). Traces each
// glyph's outline with a thick clipped brush (single stroke at any weight) while
// a pencil rides the exact draw point; fills settle in behind. Fonts come from
// the host via the chalk-assets port so both the admin (Kalam) and the Electron
// classroom (Caveat/Chalkduster) share one renderer.

/** Flatten authored HTML to plain text (block/`<br>` → newlines). */
function htmlToText(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, '');
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  tmp.querySelectorAll('p, div, li').forEach((el) => el.append('\n'));
  return (tmp.textContent ?? '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/\n+$/g, '');
}

type Align = 'left' | 'center' | 'right';
type Piece =
  | { kind: 'path'; d: string; color?: string }
  | { kind: 'text'; ch: string; x: number; y: number; color?: string; size?: number };
type SChar = { ch: string; color: string; weight: number; size: number };

interface Cmd {
  type: string;
  x?: number;
  y?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}

const r2 = (v: number) => Math.round(v * 100) / 100;

/** Re-serialize a glyph outline so each closed contour starts at its leftmost
 *  point (identical shape) — makes letters write left→right. */
function leftStartPathData(cmds: Cmd[]): string {
  const subs: Cmd[][] = [];
  let cur: Cmd[] = [];
  for (const c of cmds) {
    if (c.type === 'M') {
      if (cur.length) subs.push(cur);
      cur = [c];
    } else cur.push(c);
  }
  if (cur.length) subs.push(cur);

  const out: string[] = [];
  for (const sub of subs) {
    const m = sub[0];
    const pts: { x: number; y: number }[] = [{ x: m.x ?? 0, y: m.y ?? 0 }];
    const edgeInto: Cmd[] = [];
    for (let i = 1; i < sub.length; i++) {
      const c = sub[i];
      if (c.type === 'Z') continue;
      pts.push({ x: c.x ?? 0, y: c.y ?? 0 });
      edgeInto.push(c);
    }
    let n = pts.length;
    if (n <= 1) {
      out.push(sub.map(serialize).join(' '));
      continue;
    }
    const dup =
      Math.abs(pts[n - 1].x - pts[0].x) < 1e-3 && Math.abs(pts[n - 1].y - pts[0].y) < 1e-3;
    if (dup) n -= 1;

    const edges: Cmd[] = new Array(n);
    for (let i = 0; i < n; i++) {
      if (i < n - 1) edges[i] = edgeInto[i];
      else edges[i] = dup ? edgeInto[edgeInto.length - 1] : { type: 'L', x: pts[0].x, y: pts[0].y };
    }

    let k = 0;
    for (let i = 1; i < n; i++) if (pts[i].x < pts[k].x) k = i;

    const parts = [`M ${r2(pts[k].x)} ${r2(pts[k].y)}`];
    for (let j = 0; j < n; j++) {
      const ei = (k + j) % n;
      const dest = pts[(ei + 1) % n];
      const c = edges[ei];
      if (c.type === 'C')
        parts.push(`C ${r2(c.x1!)} ${r2(c.y1!)} ${r2(c.x2!)} ${r2(c.y2!)} ${r2(dest.x)} ${r2(dest.y)}`);
      else if (c.type === 'Q') parts.push(`Q ${r2(c.x1!)} ${r2(c.y1!)} ${r2(dest.x)} ${r2(dest.y)}`);
      else parts.push(`L ${r2(dest.x)} ${r2(dest.y)}`);
    }
    parts.push('Z');
    out.push(parts.join(' '));
  }
  return out.join(' ');
}

function serialize(c: Cmd): string {
  if (c.type === 'M') return `M ${r2(c.x!)} ${r2(c.y!)}`;
  if (c.type === 'L') return `L ${r2(c.x!)} ${r2(c.y!)}`;
  if (c.type === 'C') return `C ${r2(c.x1!)} ${r2(c.y1!)} ${r2(c.x2!)} ${r2(c.y2!)} ${r2(c.x!)} ${r2(c.y!)}`;
  if (c.type === 'Q') return `Q ${r2(c.x1!)} ${r2(c.y1!)} ${r2(c.x!)} ${r2(c.y!)}`;
  return 'Z';
}

const LINE_HEIGHT = 1.4;

interface Built {
  lines: Piece[][];
  width: number;
  height: number;
}

function build(
  resolveFont: (weight: number) => Font,
  baseFont: Font,
  fontStack: string,
  runs: StyledRun[],
  baseSize: number,
  baseWeight: number,
  baseColor: string,
  maxWidth: number,
  align: Align,
): Built {
  const padX = Math.max(1, baseSize * 0.03);
  const padY = (baseSize * (LINE_HEIGHT - 1)) / 2;

  const canvas = document.createElement('canvas');
  const mctx = canvas.getContext('2d');
  // Measure at each stretch's own size (so bigger words get room); a whole line
  // of one size measures exactly like the old single call → unstyled text is
  // pixel-identical. Weight uses the base metrics (a bold run is a hair tight —
  // the same behaviour whole-bold text already ships).
  const measure = (s: string, sz: number) => {
    if (!mctx) return s.length * sz * 0.5;
    mctx.font = `${sz}px ${fontStack}`;
    return mctx.measureText(s).width;
  };
  const str = (cs: SChar[]) => cs.map((c) => c.ch).join('');
  const measureChars = (cs: SChar[]): number => {
    let w = 0;
    let i = 0;
    while (i < cs.length) {
      let j = i;
      while (j < cs.length && cs[j].size === cs[i].size) j++;
      w += measure(str(cs.slice(i, j)), cs[i].size);
      i = j;
    }
    return w;
  };

  // Flatten runs → styled chars, collapsing 2+ newlines and trimming edges to
  // match the old plain-text flatten.
  const chars: SChar[] = [];
  for (const run of runs) {
    const color = run.color ?? baseColor;
    const weight = run.weight ?? baseWeight;
    const size = run.size ?? baseSize;
    for (const ch of run.text) {
      if (ch === '\n') {
        if (chars.length && chars[chars.length - 1].ch !== '\n')
          chars.push({ ch: '\n', color: baseColor, weight: baseWeight, size: baseSize });
      } else chars.push({ ch, color, weight, size });
    }
  }
  while (chars.length && chars[0].ch === '\n') chars.shift();
  while (chars.length && chars[chars.length - 1].ch === '\n') chars.pop();

  const paras: SChar[][] = [];
  let acc: SChar[] = [];
  for (const c of chars) {
    if (c.ch === '\n') {
      paras.push(acc);
      acc = [];
    } else acc.push(c);
  }
  paras.push(acc);

  const rawLines: SChar[][] = [];
  for (const para of paras) {
    const words: SChar[][] = [];
    let word: SChar[] = [];
    for (const c of para) {
      if (c.ch === ' ') {
        words.push(word);
        word = [];
      } else word.push(c);
    }
    words.push(word);

    let line: SChar[] = [];
    for (const wd of words) {
      const sep: SChar[] = line.length
        ? [{ ch: ' ', color: baseColor, weight: baseWeight, size: baseSize }]
        : [];
      const test = [...line, ...sep, ...wd];
      if (line.length && padX * 2 + measureChars(test) > maxWidth) {
        rawLines.push(line);
        line = wd;
      } else line = test;
    }
    rawLines.push(line);
  }

  let yCursor = padY;
  const lines = rawLines.map((lineChars) => {
    const maxSize = lineChars.reduce((m, c) => Math.max(m, c.size), baseSize);
    const ascentPx = baseFont.ascender * (maxSize / baseFont.unitsPerEm);
    const baseline = yCursor + ascentPx;
    yCursor += maxSize * LINE_HEIGHT;

    // Per-char x = leading + prefix width, measured within same-size segments.
    const xs = new Array<number>(lineChars.length);
    let lineW = 0;
    let i = 0;
    while (i < lineChars.length) {
      let j = i;
      while (j < lineChars.length && lineChars[j].size === lineChars[i].size) j++;
      const segSize = lineChars[i].size;
      for (let k = i; k < j; k++) xs[k] = lineW + measure(str(lineChars.slice(i, k)), segSize);
      lineW += measure(str(lineChars.slice(i, j)), segSize);
      i = j;
    }

    const originX =
      align === 'center'
        ? Math.max(padX, (maxWidth - lineW) / 2)
        : align === 'right'
          ? Math.max(padX, maxWidth - padX - lineW)
          : padX;

    const pieces: Piece[] = [];
    for (let k = 0; k < lineChars.length; k++) {
      const c = lineChars[k];
      if (c.ch === ' ') continue;
      const x = originX + xs[k];
      const gf = resolveFont(c.weight);
      if (gf.charToGlyphIndex(c.ch) !== 0) {
        const cmds = (gf.getPath(c.ch, x, baseline, c.size) as unknown as { commands: Cmd[] }).commands;
        const d = leftStartPathData(cmds);
        if (d && d.length > 2) pieces.push({ kind: 'path', d, color: c.color });
      } else {
        pieces.push({ kind: 'text', ch: c.ch, x, y: baseline, color: c.color, size: c.size });
      }
    }
    return pieces;
  });

  return { lines, width: maxWidth, height: yCursor + padY };
}

/** Pencil drawn with its writing tip at local (0,0); the caller aims it. */
function PencilNib({ u }: { u: number }) {
  const hw = u * 0.085;
  const woodTo = u * 0.2;
  const bodyTo = u * 1.05;
  const ferruleTo = u * 1.18;
  const eraserTo = u * 1.34;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <g>
        <path d={`M0 0 L ${-hw * 0.45} ${-u * 0.08} L ${hw * 0.45} ${-u * 0.08} Z`} fill="#2b2b2b" />
        <path
          d={`M ${-hw * 0.45} ${-u * 0.08} L ${-hw} ${-woodTo} L ${hw} ${-woodTo} L ${hw * 0.45} ${-u * 0.08} Z`}
          fill="#e8c79a"
          stroke="#caa776"
          strokeWidth={u * 0.012}
        />
        <rect x={-hw} y={-bodyTo} width={hw * 2} height={bodyTo - woodTo} fill="#f5b301" />
        <rect x={-hw} y={-bodyTo} width={hw * 0.66} height={bodyTo - woodTo} fill="#e0a200" />
        <rect x={-hw} y={-ferruleTo} width={hw * 2} height={ferruleTo - bodyTo} fill="#cfd3d6" />
        <rect x={-hw} y={-eraserTo} width={hw * 2} height={eraserTo - ferruleTo} rx={hw * 0.5} fill="#e8788a" />
      </g>
    </g>
  );
}

export function HandwritingText({
  html,
  className,
  style,
  onDone,
  paused = false,
  speed = 0.3,
  showPen = true,
  staticRender = false,
  targetMs,
}: {
  html: string;
  className?: string;
  style?: React.CSSProperties;
  onDone?: () => void;
  paused?: boolean;
  speed?: number;
  showPen?: boolean;
  /** Render the finished handwriting (fully inked, no animation/pen) — the
   *  settled state after a write, so text never swaps back to a normal font. */
  staticRender?: boolean;
  /** Target total write duration (ms). When set, the whole glyph stream is
   *  time-scaled to fill it (synced to the teacher's speaking pace); clamped so
   *  a long dwell doesn't crawl and a rapid burst stays legible. */
  targetMs?: number;
}) {
  const runs = useMemo(() => htmlToRuns(html), [html]);
  const text = useMemo(() => htmlToText(html), [html]);
  const size = typeof style?.fontSize === 'number' ? style.fontSize : parseFloat(String(style?.fontSize ?? '')) || 16;
  const color = (style?.color as string | undefined) ?? '#2f2a22';
  const weight = typeof style?.fontWeight === 'number' ? style.fontWeight : 400;
  const family = style?.fontFamily as string | undefined;
  const fontStack = family && family.trim() ? family : 'cursive';
  const align: Align =
    style?.textAlign === 'center' ? 'center' : style?.textAlign === 'right' ? 'right' : 'left';

  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);
  const [fonts, setFonts] = useState<Map<string, Font>>(new Map());
  // The chalk TTF registered as a CSS family, so measureText lays glyphs out
  // with the SAME font they're drawn from (`{family: null}` = registration
  // unavailable → fall back to the authored stack). Layout waits for this to
  // settle so a mid-write rebuild never restarts the ink.
  const [chalkFace, setChalkFace] = useState<{ family: string | null } | null>(null);
  const [failed, setFailed] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    let on = true;
    const baseUrl = chalkFontUrl(family, weight);
    const urls = new Set<string>();
    if (baseUrl) urls.add(baseUrl);
    for (const r of runs) {
      const u = chalkFontUrl(family, r.weight ?? weight);
      if (u) urls.add(u);
    }
    if (!urls.size) {
      setFailed(true);
      onDoneRef.current?.();
      return () => {
        on = false;
      };
    }
    Promise.all([...urls].map((u) => loadChalkFont(u).then((f) => [u, f] as const)))
      .then((pairs) => on && setFonts(new Map(pairs)))
      .catch(() => on && (setFailed(true), onDoneRef.current?.()));
    if (baseUrl)
      loadChalkFace(baseUrl)
        .then((fam) => on && setChalkFace({ family: fam }))
        .catch(() => on && setChalkFace({ family: null }));
    document.fonts?.ready.then(() => on && setFontsReady(true)).catch(() => {});
    return () => {
      on = false;
    };
  }, [family, weight, runs]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Ignore transient zero-width reports (display flips, mid-layout measures):
    // collapsing `width` would null the layout and ABORT a write in progress.
    const update = () => {
      const w = el.clientWidth;
      if (w > 4) setWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure with the chalk family itself when registered; the authored stack is
  // only the fallback (and the face for stray <text> glyph-miss pieces).
  const measureStack = chalkFace?.family ? `"${chalkFace.family}", ${fontStack}` : fontStack;
  const baseUrl = chalkFontUrl(family, weight);
  const baseFont = baseUrl ? fonts.get(baseUrl) ?? null : null;
  const rawBuilt = useMemo(
    () => {
      if (!baseFont || !chalkFace || width <= 4 || !runs.some((r) => r.text.trim())) return null;
      const resolve = (w: number) => {
        const u = chalkFontUrl(family, w);
        return (u && fonts.get(u)) || baseFont;
      };
      return build(resolve, baseFont, measureStack, runs, size, weight, color, width, align);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fonts, baseFont, chalkFace, width, runs, size, weight, color, align, measureStack, family, fontsReady],
  );

  // Stabilize the layout's identity: rebuild triggers (fonts.ready resolving,
  // ResizeObserver jitter) usually produce an IDENTICAL layout, and a new
  // object identity would re-run the animation effect below — cancelling the
  // in-flight ink mid-write. Only adopt a rebuild whose content actually
  // differs.
  const builtRef = useRef<Built | null>(null);
  const builtSigRef = useRef('');
  const sig = rawBuilt
    ? `${rawBuilt.width}|${rawBuilt.height}|${rawBuilt.lines
        .map((l) =>
          l
            .map((p) =>
              p.kind === 'path'
                ? `${p.d}~${p.color ?? ''}`
                : `${p.ch}@${p.x},${p.y}~${p.color ?? ''}~${p.size ?? ''}`,
            )
            .join(';'),
        )
        .join('\n')}`
    : '';
  if (!rawBuilt) {
    builtRef.current = null;
    builtSigRef.current = '';
  } else if (builtSigRef.current !== sig) {
    builtRef.current = rawBuilt;
    builtSigRef.current = sig;
  }
  const built = builtRef.current;

  useLayoutEffect(() => {
    if (staticRender) return; // fully-inked static render, nothing to animate
    const svg = svgRef.current;
    if (!svg || !built) return;
    const items = Array.from(svg.querySelectorAll<SVGElement>('[data-anim]'));
    if (!items.length) {
      onDoneRef.current?.();
      return;
    }

    const inv = 1 / Math.max(0.05, speed * 2); // 2× handwriting speed (user 2026-08-12)
    const brush = Math.max(2.2, size * 0.15);
    const running: Animation[] = [];
    const GAP = 18 * inv;
    type Seg =
      | { kind: 'glyph'; stroke: SVGPathElement; len: number; start: number; dur: number }
      | { kind: 'text'; node: SVGTextElement; start: number; dur: number };

    // PASS 1 — geometry + base (unscaled) per-item duration. Base total lets us
    // derive a single time-scale so the whole stream fills `targetMs` when set.
    type Raw =
      | { kind: 'glyph'; stroke: SVGPathElement; fillP: SVGPathElement; len: number; dur: number }
      | { kind: 'text'; node: SVGTextElement; dur: number };
    const raws: Raw[] = [];
    let baseTotal = 0;
    for (const item of items) {
      if (item.tagName.toLowerCase() === 'text') {
        const dur = 150 * inv;
        raws.push({ kind: 'text', node: item as SVGTextElement, dur });
        baseTotal += dur + GAP;
        continue;
      }
      const g = item as SVGGElement;
      const stroke = g.querySelector<SVGPathElement>('path[data-stroke]')!;
      const fillP = g.querySelector<SVGPathElement>('path[data-fill]')!;
      const len = stroke.getTotalLength() || 1;
      const dur = Math.min(1100, Math.max(70, len * 1.4 * inv));
      stroke.style.stroke = fillP.getAttribute('fill') || color;
      raws.push({ kind: 'glyph', stroke, fillP, len, dur });
      baseTotal += dur + GAP;
    }

    // Time-scale to the teacher's SPEAKING pace: fill `targetMs` (the spoken
    // duration) if given. Wide clamp (0.4×–8×) so a slowly-spoken line genuinely
    // writes slow and finishes WITH the speech, not before it (user 2026-08-14).
    let factor = targetMs && baseTotal > 0 ? Math.min(30, targetMs / baseTotal) : 1;
    // MAX handwriting SPEED (user 2026-08-16): never write faster than
    // MIN_MS_PER_GLYPH per glyph — some reveals flashed past too fast for the eye.
    // NO minimum speed: a slowly-spoken line may write as slow as it likes.
    const MIN_MS_PER_GLYPH = 95;
    if (baseTotal > 0) factor = Math.max(factor, (items.length * MIN_MS_PER_GLYPH) / baseTotal);
    const gap = GAP * factor;

    // PASS 2 — build the WAAPI animations with scaled timings.
    const segs: Seg[] = [];
    let delay = 0;
    for (const r of raws) {
      if (r.kind === 'text') {
        const dur = r.dur * factor;
        r.node.style.fillOpacity = '0';
        running.push(
          r.node.animate([{ fillOpacity: 0 }, { fillOpacity: 1 }], {
            duration: dur,
            delay,
            fill: 'forwards',
            easing: 'ease-out',
          }),
        );
        segs.push({ kind: 'text', node: r.node, start: delay, dur });
        delay += dur + gap;
        continue;
      }
      const { stroke, fillP, len } = r;
      const dur = r.dur * factor;
      stroke.style.stroke = color;
      stroke.style.strokeWidth = String(brush);
      stroke.style.strokeDasharray = String(len);
      stroke.style.strokeDashoffset = String(len);
      fillP.style.fillOpacity = '0';
      running.push(
        stroke.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], {
          duration: dur,
          delay,
          fill: 'forwards',
          easing: 'linear',
        }),
        fillP.animate([{ fillOpacity: 0 }, { fillOpacity: 1 }], {
          duration: Math.min(180, dur * 0.5),
          delay: delay + dur * 0.55,
          fill: 'forwards',
          easing: 'ease-out',
        }),
      );
      segs.push({ kind: 'glyph', stroke, len, start: delay, dur });
      delay += dur + gap;
    }

    const total = delay;
    const nib = showPen ? svg.querySelector<SVGGElement>('g[data-nib]') : null;
    let raf = 0;
    let lastTs = 0;
    let acc = 0;
    let done = false;
    let hx = NaN;
    let hy = NaN;
    let rot = 0;
    let rotTarget = 0;
    let nextRot = 500 + Math.random() * 800;
    let wasPaused = false;

    const tick = (ts: number) => {
      // Pause: freeze the acc clock and the WAAPI ink animations together.
      // Only touch animations that are actually in flight — pause()/play() on a
      // FINISHED fill-forwards animation would rewind it and replay its glyph.
      if (pausedRef.current) {
        if (!wasPaused) {
          wasPaused = true;
          running.forEach((a) => {
            if (a.playState === 'running') a.pause();
          });
        }
        lastTs = ts;
        raf = requestAnimationFrame(tick);
        return;
      }
      if (wasPaused) {
        wasPaused = false;
        running.forEach((a) => {
          if (a.playState === 'paused') a.play();
        });
      }
      if (lastTs) acc += ts - lastTs;
      lastTs = ts;

      if (nib) {
        const seg = segs.find((s) => acc >= s.start && acc <= s.start + s.dur);
        if (seg) {
          let px = 0;
          let py = 0;
          if (seg.kind === 'glyph') {
            const frac = Math.max(0, Math.min(1, (acc - seg.start) / seg.dur));
            const pt = seg.stroke.getPointAtLength(seg.len * frac);
            px = pt.x;
            py = pt.y;
          } else {
            px = seg.node.x.baseVal.getItem(0)?.value ?? 0;
            py = seg.node.y.baseVal.getItem(0)?.value ?? 0;
          }
          const L = size * 1.05;
          const ax = px + L * 0.5;
          const ay = py - L * 0.87;
          if (Number.isNaN(hx)) {
            hx = ax;
            hy = ay;
          } else {
            hx += (ax - hx) * 0.15;
            hy += (ay - hy) * 0.15;
          }
          const pivot = (Math.atan2(hy - py, hx - px) * 180) / Math.PI + 90;
          if (acc >= nextRot) {
            if (Math.random() < 0.6) rotTarget = (Math.random() * 2 - 1) * 8;
            nextRot = acc + 600 + Math.random() * 1500;
          }
          rot += (rotTarget - rot) * 0.1;
          nib.style.opacity = '1';
          nib.setAttribute('transform', `translate(${px} ${py}) rotate(${pivot + rot})`);
        }
      }

      if (acc >= total) {
        if (nib) nib.style.opacity = '0';
        if (!done) {
          done = true;
          onDoneRef.current?.();
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    if (nib) nib.style.opacity = '0';
    raf = requestAnimationFrame(tick);

    return () => {
      running.forEach((a) => a.cancel());
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, speed, size, color, showPen, staticRender, targetMs]);

  // Font unavailable → plain static text (host hands off; onDone already fired).
  if (failed) {
    return (
      <div
        className={className}
        style={{ ...style, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {text}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={className} style={{ ...style, position: 'relative' }}>
      {built ? (
        <svg
          ref={svgRef}
          width={built.width}
          height={built.height}
          viewBox={`0 0 ${built.width} ${built.height}`}
          style={{ display: 'block', overflow: 'visible' }}
        >
          <defs>
            <filter id={`${uid}-nib-shadow`} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy={size * 0.03} stdDeviation={size * 0.03} floodColor="rgba(0,0,0,0.35)" />
            </filter>
            {(() => {
              let ci = 0;
              return built.lines.flatMap((line) =>
                line
                  .filter((p): p is Extract<Piece, { kind: 'path' }> => p.kind === 'path')
                  .map((g) => {
                    const id = `${uid}-clip-${ci++}`;
                    return (
                      <clipPath key={id} id={id}>
                        <path d={g.d} />
                      </clipPath>
                    );
                  }),
              );
            })()}
          </defs>
          {(() => {
            let ci = 0;
            return built.lines.flatMap((line, li) =>
              line.map((g, idx) => {
                if (g.kind === 'text') {
                  return (
                    <text
                      key={`${li}-${idx}`}
                      data-anim=""
                      x={g.x}
                      y={g.y}
                      fill={g.color ?? color}
                      fontFamily={measureStack}
                      fontSize={g.size ?? size}
                      style={{ fillOpacity: staticRender ? 1 : 0 }}
                    >
                      {g.ch}
                    </text>
                  );
                }
                const clipId = `${uid}-clip-${ci++}`;
                return (
                  <g key={`${li}-${idx}`} data-anim="" data-glyph="">
                    <path
                      data-stroke=""
                      d={g.d}
                      fill="none"
                      stroke="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      clipPath={`url(#${clipId})`}
                    />
                    <path data-fill="" d={g.d} fill={g.color ?? color} style={{ fillOpacity: staticRender ? 1 : 0 }} />
                  </g>
                );
              }),
            );
          })()}
          {showPen && !staticRender && (
            <g data-nib="" style={{ opacity: 0 }}>
              <g filter={`url(#${uid}-nib-shadow)`}>
                <PencilNib u={size * 0.82} />
              </g>
            </g>
          )}
        </svg>
      ) : (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color }}>{text}</div>
      )}
    </div>
  );
}
